import { useEffect, useRef, useState } from 'react'
import { looksBlurry, prepareFile, type CapturedPage } from '@core/capture'
import { queueSend } from '@core/documents'
import type { FamilySession } from '@core/session'
import { Button, Card } from '@design/components'
import { VoiceNote } from './VoiceNote'
import { Overlay } from '@design/Overlay'

/**
 * The most important interaction in the product.
 *
 * The competition is WhatsApp: open, snap, send, about five seconds, no
 * questions. Every element here is in service of beating that, so the rules
 * are strict — no category, no space, no confirmation of what the AI read, no
 * error a parent cannot act on. Confirmation is shown BEFORE the upload
 * finishes, because the parent's job ends at the shutter.
 */
type Stage = 'capturing' | 'review' | 'done'

export function SendFlow({
  session,
  onClose,
}: {
  session: FamilySession
  onClose: () => void
}) {
  const [stage, setStage] = useState<Stage>('capturing')
  const [pages, setPages] = useState<CapturedPage[]>([])
  const [blurWarning, setBlurWarning] = useState(false)
  const [voice, setVoice] = useState<Blob | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // A second input without `capture`, so iOS offers Files and the photo
  // library instead of opening the camera. CLAUDE.md's platform table asks for
  // ordinary file picking as well, and a bill that arrived as a PDF should not
  // have to be photographed off a screen.
  const fileRef = useRef<HTMLInputElement>(null)

  // Open the camera the instant the screen mounts. Not a screen with a button
  // on it — the camera itself. This is worth two whole seconds.
  useEffect(() => {
    inputRef.current?.click()
  }, [])

  async function onFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    e.target.value = ''
    if (files.length === 0) {
      // Camera cancelled with nothing captured yet: leave rather than strand
      // them on an empty screen.
      if (pages.length === 0) onClose()
      return
    }
    setBusy(true)
    // A file that cannot be read is dropped silently rather than shown as an
    // error: rule 21, capture never fails visibly at the sender.
    const prepared = (await Promise.all(files.map((f) => prepareFile(f).catch(() => null)))).filter(
      (p): p is CapturedPage => p !== null,
    )
    setBusy(false)
    if (prepared.length === 0 && pages.length === 0) {
      onClose()
      return
    }
    setPages((prev) => [...prev, ...prepared])
    setBlurWarning(prepared.some(looksBlurry))
    setStage('review')
  }

  async function send() {
    // Queue and confirm immediately. The upload happens behind this screen and
    // may not even have started when the parent sees the checkmark.
    await queueSend({
      spaceId: session.spaces[0]?.id ?? '',
      uploadedBy: session.userId,
      // The note travels last, so page one stays page one.
      pages: [...pages.map((p) => p.blob), ...(voice ? [voice] : [])],
    })
    setStage('done')
  }

  if (stage === 'done') return <SentConfirmation onClose={onClose} />

  return (
    <Overlay>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        onChange={onFiles}
        className="hidden"
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf,.pdf"
        multiple
        onChange={onFiles}
        className="hidden"
      />

      <header className="shrink-0 pad-safe-top px-4 pb-2 flex items-center justify-between">
        <button onClick={onClose} className="text-muted py-2 px-1">
          Annuleren
        </button>
        <span className="text-sm text-muted">
          {pages.length > 0 && `${pages.length} ${pages.length === 1 ? 'pagina' : "pagina's"}`}
        </span>
      </header>

      <div className="flex-1 scroll-area px-4">
        {busy && <p className="text-muted text-center py-8">Even geduld…</p>}

        {stage === 'review' && (
          <>
            {blurWarning && (
              <Card className="mb-4 border-warn/40 bg-warn/5">
                <p className="font-medium mb-1">Beetje wazig</p>
                <p className="text-sm text-muted">
                  Je kan het opnieuw proberen, of gewoon versturen. Dat mag ook.
                </p>
              </Card>
            )}
            <div className="grid grid-cols-3 gap-2 pb-4">
              {pages.map((p, i) => (
                <Thumb key={i} page={p} index={i} onRemove={() => remove(i)} />
              ))}
            </div>
          </>
        )}
      </div>

      {stage === 'review' && (
        <div className="shrink-0 px-4 pb-4 pad-safe-bottom space-y-3">
          <VoiceNote
            recorded={voice}
            onRecorded={setVoice}
            onClear={() => setVoice(null)}
          />
          <Button size="large" tone="secondary" onClick={() => inputRef.current?.click()}>
            Nog een pagina
          </Button>
          <Button tone="quiet" onClick={() => fileRef.current?.click()}>
            Of kies een bestand
          </Button>
          <Button size="large" onClick={send} disabled={pages.length === 0}>
            Versturen
          </Button>
        </div>
      )}
    </Overlay>
  )

  function remove(index: number) {
    setPages((prev) => prev.filter((_, i) => i !== index))
  }
}

function Thumb({
  page,
  index,
  onRemove,
}: {
  page: CapturedPage
  index: number
  onRemove: () => void
}) {
  const [url, setUrl] = useState('')
  useEffect(() => {
    const u = URL.createObjectURL(page.blob)
    setUrl(u)
    return () => URL.revokeObjectURL(u)
  }, [page.blob])

  return (
    <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-line">
      {url && <img src={url} alt={`Pagina ${index + 1}`} className="w-full h-full object-cover" />}
      <button
        onClick={onRemove}
        aria-label={`Pagina ${index + 1} verwijderen`}
        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-ink/80 text-canvas text-sm grid place-items-center"
      >
        ×
      </button>
    </div>
  )
}

function SentConfirmation({ onClose }: { onClose: () => void }) {
  // Auto-dismiss so the parent is not left holding a screen. Long enough to
  // register the checkmark, short enough not to feel like a step.
  useEffect(() => {
    const t = setTimeout(onClose, 2200)
    return () => clearTimeout(t)
  }, [onClose])

  return (
    <Overlay className="items-center justify-center px-6">
      <div className="w-20 h-20 rounded-full bg-ok/10 grid place-items-center mb-5">
        <svg viewBox="0 0 24 24" className="w-10 h-10 text-ok" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="m4 12.5 5 5L20 7" />
        </svg>
      </div>
      <p className="text-2xl font-semibold mb-1">Verstuurd</p>
      <p className="text-muted text-center">Je hoeft niets meer te doen.</p>
    </Overlay>
  )
}
